import React, { useEffect, useState } from "react";
import { Box, Typography, styled } from "@mui/material";
import { CalculatingFit } from "./CalculatingFit";
import { SparkleGlyph } from "./SparkleGlyph";
import { CenterPanelContainer } from "./styles";
import { COLUMN_HEIGHT } from "../../constants";
import { FlipIcon } from "../icons/FlipIcon";
import type { MSHatData } from "../../types";
import { FacetInfoModal } from "../../../components/FacetInfoModal";
import type { FacetDefinition } from "../../../components/FacetInfoModal";
import {
  deriveHatFit,
  deriveHatFitMessage,
  HatCoreTiles,
  HatWorkValues,
} from "./hatFit";

import { toSrc } from "../../utils/toSrc";

import seeYourFitIconImport from "./see-your-fit.svg";

const seeYourFitIcon = toSrc(seeYourFitIconImport as string | { src: string });

interface BackCenterPanelProps {
  hat: MSHatData;
  onFlip: () => void;
  cmsFacets?: FacetDefinition[];
  userHighInterestRiasec?: string[];
  userLowInterestRiasec?: string[];
  userSomeInterestRiasec?: string[];
  userTopWorkValues?: string[];
  onDiscoverCore?: () => void;
  onDiscoverWorkValues?: () => void;
  /** When true, render fit results without showing the "See Your Fit" button or calculating animation. */
  fitRevealed?: boolean;
  /** Fires once the calculating animation completes (i.e. the user just revealed). */
  onFitRevealed?: () => void;
  clusterColor?: string;
  /**
   * Renders the Flip control. Off for callers that show this panel on its own,
   * where there is no front face to flip back to — the Climb Plan hat picker's
   * detail modal, for one.
   */
  showFlip?: boolean;
  /**
   * Renders the card chrome: the hat image flipped behind a black scrim, the
   * black fill and the rounded border.
   *
   * On the hat page this panel IS the back of a card, so the chrome is the
   * point. Somewhere it is only borrowed for its Core and Work Values — the
   * Climb Plan hat detail modal — the image reads as a second, competing
   * backdrop behind the one the modal already has. Off, the panel is
   * transparent and takes the colour of whatever hosts it.
   */
  showCardChrome?: boolean;
  /**
   * How the sections are laid out inside the panel.
   *
   * "card" is the hat card's own back face: headings and lists centred, with a
   * rule between each section — it reads as a card because it IS one.
   *
   * "panel" is for a caller that has borrowed the Core and Work Values for a
   * column of its own (the Climb Plan hat details, 1119-11704). Everything is
   * left-aligned against that column's edge and the rules are gone, because the
   * sections are already separated by the space around them.
   *
   * A prop rather than a second component: the scoring, the tiers and the Fit
   * badges are the same reading either way, and two copies of that would be two
   * answers to "does this hat fit me".
   */
  layout?: "card" | "panel";
}

// --- Styled components ---

const CardContainer = styled(Box, {
  label: "BackCenterCard",
  shouldForwardProp: (prop) => prop !== "borderColor" && prop !== "chrome",
})<{ borderColor?: string; chrome?: boolean }>(({ borderColor, chrome }) => ({
  position: "relative",
  width: "100%",
  height: "100%",
  // The card face clips to itself; a bare panel must not, or the fit verdict
  // at the bottom of the 569px content is cut off with no way to scroll to it.
  // A caller in that mode reserves COLUMN_HEIGHT for it.
  overflow: chrome === false ? "visible" : "hidden",
  ...(chrome !== false && {
    borderRadius: 16,
    backgroundColor: "#000000",
    border: "4px solid transparent",
    transition: "border-color 0.2s ease",
  }),
  ...(borderColor &&
    chrome !== false && {
      "&:has(.BackCenterFlipButton-root:hover)": {
        borderColor,
      },
    }),
}));

// The hat card image, flipped horizontally, fills the card as its backdrop.
const CardBackgroundImage = styled("img", {
  label: "BackCenterCardBackgroundImage",
})(() => ({
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  transform: "scaleX(-1)",
  pointerEvents: "none",
}));

// Transparent black overlay sits over the flipped image.
const CardBackgroundOverlay = styled(Box, {
  label: "BackCenterCardBackgroundOverlay",
})(() => ({
  position: "absolute",
  inset: 0,
  backgroundColor: "rgba(0, 0, 0, 0.75)",
  pointerEvents: "none",
}));

const CardContentPositioner = styled(Box, {
  label: "BackCenterCardContentPositioner",
})(() => ({
  position: "absolute",
  top: 0,
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 1,
}));

const CardContent = styled(Box, {
  label: "BackCenterCardContent",
  shouldForwardProp: (prop) => prop !== "panel",
})<{ panel?: boolean }>(({ panel }) => ({
  width: 368,
  height: 569,
  display: "flex",
  flexDirection: "column",
  padding: "12px 24px 88px",
  // The card face is packed to fit 569px with a flip button at the bottom.
  // A panel has neither constraint, and the design gives its sections room to
  // breathe (1119-11704).
  gap: panel ? 24 : 16,
  alignItems: "center",
  transform: `scale(calc(${COLUMN_HEIGHT} / 569px))`,
  transformOrigin: "top center",
}));

const SectionHeading = styled(Typography, {
  label: "BackCenterSectionHeading",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "16px",
  lineHeight: "24px",
  color: "#ffffff",
  textTransform: "uppercase",
  textAlign: "center",
  width: "100%",
}));

const SectionPrompt = styled(Typography, {
  label: "BackCenterSectionPrompt",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontStyle: "italic",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "22px",
  color: "#ffffff",
  textAlign: "center",
  width: "100%",
}));

// Groups a section heading with its list. In Figma the heading + list sit in a
// container with gap 8 ("Core Types" / "Work Values"); only the groups are
// separated by the outer 16px gap.
const SectionGroup = styled(Box, {
  label: "BackCenterSectionGroup",
})(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 8,
  width: "100%",
}));

const SectionDivider = styled(Box, {
  label: "BackCenterDivider",
})(() => ({
  width: 320,
  height: 1,
  backgroundColor: "rgba(145, 158, 171, 0.2)",
  flexShrink: 0,
}));

const SeeYourFitButton = styled("button", {
  label: "BackCenterSeeYourFit",
})(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  height: 48,
  minWidth: 64,
  padding: "0 16px",
  borderRadius: 8,
  border: "1px solid rgba(145, 158, 171, 0.32)",
  backgroundColor: "#ffffff",
  cursor: "pointer",
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 700,
  fontSize: "15px",
  lineHeight: "26px",
  color: "#1c252e",
  transition: "background-color 0.2s ease",
  "&:hover": { backgroundColor: "#f4f6f8" },
}));

// Fit summary: a purple chip ("Must Try" / "Might Not Like") stacked above a
// short message, shown after the fit reveal in place of the "See Your Fit"
// button.
const FitMessageGroup = styled(Box, {
  label: "BackCenterFitMessageGroup",
})(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 6,
  width: "100%",
}));

const FitChip = styled(Box, {
  label: "BackCenterFitChip",
})(() => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "3px 9px",
  borderRadius: 999,
  backgroundColor: "#8c31fc",
}));

const FitChipText = styled("span", {
  label: "BackCenterFitChipText",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 700,
  fontSize: "11px",
  lineHeight: "16px",
  color: "#ffffff",
}));

const FitMessage = styled(Typography, {
  label: "BackCenterFitMessage",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 400,
  fontSize: "12px",
  lineHeight: "18px",
  color: "rgba(255, 255, 255, 0.6)",
  textAlign: "center",
  maxWidth: 272,
}));

// Bold, full-white emphasis (hat name / fit count) inside the fit message.
const FitEmphasis = styled("strong", {
  label: "BackCenterFitEmphasis",
})(() => ({
  fontWeight: 600,
  color: "#ffffff",
}));

const FlipButton = styled("button", {
  label: "BackCenterFlipButton",
})(() => ({
  border: "none",
  padding: 0,
  outline: "none",
  "&:focus-visible": {
    outline: "2px solid #fa680d",
    outlineOffset: 2,
  },
  position: "absolute",
  bottom: 19,
  left: "50%",
  transform: "translateX(-50%)",
  width: 64,
  height: 64,
  borderRadius: "50%",
  backgroundColor: "rgba(255, 255, 255, 0.32)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  transition: "background-color 0.2s ease",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.48)",
  },
}));

FlipButton.defaultProps = { className: "BackCenterFlipButton-root" };

const FlipLabel = styled(Typography, {
  label: "BackCenterFlipLabel",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "16px",
  lineHeight: "24px",
  color: "#ffffff",
  textAlign: "center",
}));

export const BackCenterPanel = ({
  hat,
  onFlip,
  cmsFacets = [],
  userHighInterestRiasec,
  userLowInterestRiasec,
  userSomeInterestRiasec,
  userTopWorkValues,
  fitRevealed = false,
  showFlip = true,
  showCardChrome = true,
  layout = "card",
  onFitRevealed,
  clusterColor,
}: BackCenterPanelProps) => {
  const panel = layout === "panel";
  const alignSx = panel ? { textAlign: "left" as const } : undefined;
  const listSx = panel ? { justifyContent: "flex-start" } : undefined;

  const [showFit, setShowFit] = useState(fitRevealed);
  const [calculating, setCalculating] = useState(false);
  const [selectedFacet, setSelectedFacet] = useState<FacetDefinition | null>(
    null,
  );

  const openFacet = (facet?: FacetDefinition) => {
    if (facet) setSelectedFacet(facet);
  };

  useEffect(() => {
    if (fitRevealed) setShowFit(true);
  }, [fitRevealed]);

  const handleSeeYourFit = () => setCalculating(true);
  const handleCalculatingComplete = () => {
    setCalculating(false);
    setShowFit(true);
    onFitRevealed?.();
  };

  const { coreEntries, workValueEntries } = deriveHatFit({
    hat,
    cmsFacets,
    userHighInterestRiasec,
    userLowInterestRiasec,
    userSomeInterestRiasec,
    userTopWorkValues,
  });

  const userHighSet = new Set(userHighInterestRiasec || []);
  const userLowSet = new Set(userLowInterestRiasec || []);
  const userSomeSet = new Set(userSomeInterestRiasec || []);
  const userWorkValueSet = new Set(userTopWorkValues || []);

  const hasUserData =
    userHighSet.size > 0 ||
    userLowSet.size > 0 ||
    userSomeSet.size > 0 ||
    userWorkValueSet.size > 0;

  // The fit tag, the "N fits" count and the sentence under it. Derived in
  // hatFit.ts so the Climb Plan PDF prints the same verdict this card shows.
  const fit = deriveHatFitMessage({
    hat,
    cmsFacets,
    userHighInterestRiasec,
    userLowInterestRiasec,
    userSomeInterestRiasec,
    userTopWorkValues,
  });
  const hatName = hat.displayName;

  const backgroundUrl = hat.hatCard?.largeCard || hat.displayImage?.url;

  return (
    <CenterPanelContainer>
      <CardContainer borderColor={clusterColor} chrome={showCardChrome}>
        {showCardChrome && backgroundUrl && (
          <>
            <CardBackgroundImage src={backgroundUrl} alt="" />
            <CardBackgroundOverlay />
          </>
        )}
        <CardContentPositioner>
          <CardContent panel={panel}>
            {/* Core */}
            <SectionGroup>
              <Box sx={{ width: "100%" }}>
                <SectionHeading sx={alignSx}>Core</SectionHeading>
                <SectionPrompt sx={alignSx}>
                  What kind of person enjoys this work?
                </SectionPrompt>
              </Box>
              <HatCoreTiles
                entries={coreEntries}
                showFit={showFit}
                onSelectFacet={openFacet}
                sx={listSx}
              />
            </SectionGroup>

            {!panel && <SectionDivider />}

            {/* Work Values — on the CARD its flanking gaps are pulled in
                (negative margins take the 16px column gap down to 4px) and its
                internal gap shrunk, so the fit message clears the flip button.
                A panel has no flip button and no fixed height, so it keeps the
                spacing the rest of the column uses. */}
            <SectionGroup
              sx={
                panel
                  ? undefined
                  : { gap: "4px", marginTop: "-12px", marginBottom: "-12px" }
              }
            >
              <Box sx={{ width: "100%" }}>
                <SectionHeading sx={alignSx}>Work Values</SectionHeading>
                <SectionPrompt sx={alignSx}>
                  What makes this work rewarding?
                </SectionPrompt>
              </Box>
              {workValueEntries.length > 0 && (
                <HatWorkValues
                  entries={workValueEntries}
                  showFit={showFit}
                  onSelectFacet={openFacet}
                  sx={listSx}
                />
              )}
            </SectionGroup>

            {/* See Your Fit */}
            {calculating ? (
              <CalculatingFit onComplete={handleCalculatingComplete} />
            ) : !showFit && hasUserData ? (
              <>
                {!panel && <SectionDivider />}
                <SeeYourFitButton onClick={handleSeeYourFit}>
                  <img
                    src={seeYourFitIcon}
                    alt=""
                    width={24}
                    height={24}
                    style={{ flexShrink: 0 }}
                  />
                  See Your Fit
                </SeeYourFitButton>
              </>
            ) : showFit ? (
              <>
                {!panel && <SectionDivider />}
                <FitMessageGroup>
                  <FitChip>
                    <SparkleGlyph size={10} />
                    <FitChipText>{fit.label}</FitChipText>
                  </FitChip>
                  <FitMessage>
                    {fit.categoryKey === "mightNotLike" ? (
                      <>
                        You have {fit.fitLabel}.{" "}
                        <FitEmphasis>{hatName}</FitEmphasis> may not be a fit
                        for you – but explore the details of this Hat to know
                        for sure.
                      </>
                    ) : (
                      <>
                        You have <FitEmphasis>{fit.fitLabel}</FitEmphasis> for{" "}
                        <FitEmphasis>{hatName}.</FitEmphasis> {fit.tone}
                      </>
                    )}
                  </FitMessage>
                </FitMessageGroup>
              </>
            ) : null}

            {showFlip && (
              <FlipButton onClick={onFlip} aria-label="Flip card to front">
                <FlipIcon />
                <FlipLabel>Flip</FlipLabel>
              </FlipButton>
            )}
          </CardContent>
        </CardContentPositioner>
      </CardContainer>
      <FacetInfoModal
        open={!!selectedFacet}
        onClose={() => setSelectedFacet(null)}
        facet={selectedFacet}
      />
    </CenterPanelContainer>
  );
};
